import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import removeDynamicFormFieldValue from "@midland-industries/Store/Data/DynamicForms/Handlers/RemoveDynamicFormFieldValue";
import {
    DynamicFormButtonShapes,
    DynamicFormButtonShapesPill,
    DynamicFormButtonShapesRectangle,
    DynamicFormButtonShapesRounded,
    DynamicFormButtonTypes,
    DynamicFormButtonTypesOutline,
    DynamicFormButtonTypesSolid,
    DynamicFormButtonVariants,
    DynamicFormButtonVariantsPrimary,
    DynamicFormButtonVariantsSecondary,
    DynamicFormButtonVariantsTertiary,
    DynamicFormEmailRegExp,
    DynamicFormTextFieldLabelPositionsTop,
    DynamicFormThirdPartyApiTypes,
    DynamicFormThirdPartyApiTypesCustom,
} from "@midland-industries/Types/DynamicFormModels";
import DynamicForm from "@midland-industries/Widgets/DynamicForms/DynamicForm";
import DynamicFormSelect from "@midland-industries/Widgets/DynamicForms/DynamicFormSelect";
import DynamicFormTextArea from "@midland-industries/Widgets/DynamicForms/DynamicFormTextArea";
import DynamicFormTextField from "@midland-industries/Widgets/DynamicForms/DynamicFormTextField";
import React, { useState } from "react";
import { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    emailRecipients = "emailRecipients",
    successMessage = "successMessage",
    enableRecaptcha = "enableRecaptcha",
    submitButtonText = "submitButtonText",
    buttonVariant = "buttonVariant",
    buttonShape = "buttonShape",
    buttonType = "buttonType",
    sendToThirdPartyApi = "sendToThirdPartyApi",
    sendToApiType = "sendToApiType",
    customApiEndpoint = "customApiEndpoint",
    endpointRequiresAuth = "endpointRequiresAuth",
    endpointUserId = "endpointUserId",
    endpointPassword = "endpointPassword",
    displayCustomApiResponse = "displayCustomApiResponse",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.emailRecipients]: string;
        [fields.successMessage]: string;
        [fields.enableRecaptcha]: boolean;
        [fields.submitButtonText]: string;
        [fields.buttonVariant]: DynamicFormButtonVariants;
        [fields.buttonShape]: DynamicFormButtonShapes;
        [fields.buttonType]: DynamicFormButtonTypes;
        [fields.sendToThirdPartyApi]: boolean;
        [fields.sendToApiType]: DynamicFormThirdPartyApiTypes;
        [fields.customApiEndpoint]: string;
        [fields.endpointRequiresAuth]: boolean;
        [fields.endpointUserId]: string;
        [fields.endpointPassword]: string;
        [fields.displayCustomApiResponse]: boolean;
    };
}

const mapDispatchToProps = {
    removeDynamicFormFieldValue,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface ContactUsFormStyles {
    wrapper: InjectableCss;
    outerWrapper?: InjectableCss;
    innerWideWrapper?: InjectableCss;
    innerNarrowWrapper?: InjectableCss;
}

export const contactUsFormStyles: ContactUsFormStyles = {
    wrapper: {
        css: css`
            width: 100%;
            margin-bottom: 30px;
        `,
    },
    outerWrapper: {
        css: css`
            width: 100%;
        `,
    },
    innerWideWrapper: {
        css: css`
            width: 80%;
            margin-bottom: 1em;
        `,
    },
    innerNarrowWrapper: {
        css: css`
            width: 40%;
            margin-bottom: 1em;
        `,
    },
};

const styles = contactUsFormStyles;

const ContactUsForm: FC<Props> = props => {
    const { id, fields, history, removeDynamicFormFieldValue } = props;
    const {
        emailRecipients,
        successMessage,
        enableRecaptcha,
        submitButtonText,
        buttonVariant,
        buttonShape,
        buttonType,
        sendToThirdPartyApi,
        sendToApiType,
        customApiEndpoint,
        endpointRequiresAuth,
        endpointUserId,
        endpointPassword,
        displayCustomApiResponse,
    } = fields;

    const [selectedReason, setSelectedReason] = useState<string>("");

    const formName = "Authenticated Contact Us Form";

    const handleReasonChange: React.ChangeEventHandler<HTMLSelectElement> = event => {
        const prevSelectedReason = selectedReason.toString();
        removeDynamicFormElementsFromState(prevSelectedReason);

        const newSelectedReason = event.target.value;
        setSelectedReason(newSelectedReason);

        switch (newSelectedReason) {
            case "Return":
                history.push("/my-account/Orders");
                break;
            case "Request a Quote":
                history.push("/Contact/Request-a-Quote");
                break;
            default:
                break;
        }
    };

    const removeDynamicFormElementsFromState = (prevSelectedReason: string) => {
        switch (prevSelectedReason) {
            case "Invoice Request":
                removeDynamicFormFieldValue({
                    formName,
                    formFieldKey: "invoice-number",
                });
                removeDynamicFormFieldValue({
                    formName,
                    formFieldKey: "purchase-order-number",
                });
                removeDynamicFormFieldValue({
                    formName,
                    formFieldKey: "order-number",
                });
                break;
            case "Order Status":
                removeDynamicFormFieldValue({
                    formName,
                    formFieldKey: "purchase-order-number",
                });
                removeDynamicFormFieldValue({
                    formName,
                    formFieldKey: "order-number",
                });
                break;
            default:
                break;
        }
    };

    const generateDynamicContent = () => {
        let tempDynamicContent = <></>;
        switch (selectedReason) {
            case "Invoice Request":
                tempDynamicContent = (
                    <StyledWrapper {...styles.outerWrapper}>
                        <StyledWrapper {...styles.innerNarrowWrapper}>
                            <DynamicFormTextField
                                fields={{
                                    key: "invoice-number",
                                    labelPosition: DynamicFormTextFieldLabelPositionsTop,
                                    label: "Invoice Number",
                                    hintText: "",
                                    isRequired: false,
                                    enableMinLength: false,
                                    minLength: 0,
                                    enableMaxLength: false,
                                    maxLength: 100,
                                    validateAsPhoneNumber: false,
                                    validateAsEmailAddress: false,
                                }}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.innerNarrowWrapper}>
                            <DynamicFormTextField
                                fields={{
                                    key: "purchase-order-number",
                                    labelPosition: DynamicFormTextFieldLabelPositionsTop,
                                    label: "Purchase Order Number",
                                    hintText: "",
                                    isRequired: true,
                                    enableMinLength: false,
                                    minLength: 0,
                                    enableMaxLength: false,
                                    maxLength: 100,
                                    validateAsPhoneNumber: false,
                                    validateAsEmailAddress: false,
                                }}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.innerNarrowWrapper}>
                            <DynamicFormTextField
                                fields={{
                                    key: "order-number",
                                    labelPosition: DynamicFormTextFieldLabelPositionsTop,
                                    label: "Order Number",
                                    hintText: "",
                                    isRequired: false,
                                    enableMinLength: false,
                                    minLength: 0,
                                    enableMaxLength: false,
                                    maxLength: 100,
                                    validateAsPhoneNumber: false,
                                    validateAsEmailAddress: false,
                                }}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                );
                break;
            case "Order Status":
                tempDynamicContent = (
                    <StyledWrapper {...styles.outerWrapper} data-test-selector="dynamicContentOuterWrapper">
                        <StyledWrapper {...styles.innerNarrowWrapper}>
                            <DynamicFormTextField
                                fields={{
                                    key: "purchase-order-number",
                                    labelPosition: DynamicFormTextFieldLabelPositionsTop,
                                    label: "Purchase Order Number",
                                    hintText: "",
                                    isRequired: true,
                                    enableMinLength: false,
                                    minLength: 0,
                                    enableMaxLength: false,
                                    maxLength: 100,
                                    validateAsPhoneNumber: false,
                                    validateAsEmailAddress: false,
                                }}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.innerNarrowWrapper}>
                            <DynamicFormTextField
                                fields={{
                                    key: "order-number",
                                    labelPosition: DynamicFormTextFieldLabelPositionsTop,
                                    label: "Order Number",
                                    hintText: "",
                                    isRequired: false,
                                    enableMinLength: false,
                                    minLength: 0,
                                    enableMaxLength: false,
                                    maxLength: 100,
                                    validateAsPhoneNumber: false,
                                    validateAsEmailAddress: false,
                                }}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                );
                break;
            default:
                break;
        }

        return tempDynamicContent;
    };

    const dynamicContent = generateDynamicContent();

    return (
        <StyledWrapper {...styles.wrapper}>
            <DynamicForm
                id={id}
                fields={{
                    formName,
                    emailRecipients,
                    submitButtonText,
                    successMessage,
                    buttonVariant,
                    buttonShape,
                    buttonType,
                    showResetButton: false,
                    enableRecaptcha,
                    sendToThirdPartyApi,
                    sendToApiType,
                    customApiEndpoint,
                    endpointRequiresAuth,
                    endpointUserId,
                    endpointPassword,
                    displayCustomApiResponse,
                }}
            >
                <StyledWrapper {...styles.innerNarrowWrapper}>
                    <DynamicFormTextField
                        fields={{
                            key: "first-name",
                            labelPosition: DynamicFormTextFieldLabelPositionsTop,
                            label: "First Name",
                            hintText: "",
                            isRequired: true,
                            enableMinLength: false,
                            minLength: 0,
                            enableMaxLength: false,
                            maxLength: 100,
                            validateAsPhoneNumber: false,
                            validateAsEmailAddress: false,
                        }}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.innerNarrowWrapper}>
                    <DynamicFormTextField
                        fields={{
                            key: "last-name",
                            labelPosition: DynamicFormTextFieldLabelPositionsTop,
                            label: "Last Name",
                            hintText: "",
                            isRequired: true,
                            enableMinLength: false,
                            minLength: 0,
                            enableMaxLength: false,
                            maxLength: 100,
                            validateAsPhoneNumber: false,
                            validateAsEmailAddress: false,
                        }}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.innerNarrowWrapper}>
                    <DynamicFormTextField
                        fields={{
                            key: "email",
                            labelPosition: DynamicFormTextFieldLabelPositionsTop,
                            label: "Email",
                            hintText: "",
                            isRequired: true,
                            enableMinLength: false,
                            minLength: 0,
                            enableMaxLength: false,
                            maxLength: 100,
                            validateAsPhoneNumber: false,
                            validateAsEmailAddress: true,
                        }}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.innerNarrowWrapper}>
                    <DynamicFormSelect
                        handleOnChange={handleReasonChange}
                        fields={{
                            key: "reason",
                            hasLabel: true,
                            label: "Reason",
                            hintText: "",
                            isRequired: true,
                            options: [
                                {
                                    fields: {
                                        label: "Account Updates",
                                        value: "Account Updates",
                                    },
                                },
                                {
                                    fields: {
                                        label: "Invoice Request",
                                        value: "Invoice Request",
                                    },
                                },
                                {
                                    fields: {
                                        label: "Order Status",
                                        value: "Order Status",
                                    },
                                },
                                {
                                    fields: {
                                        label: "Return",
                                        value: "Return",
                                    },
                                },
                                {
                                    fields: {
                                        label: "Request a Quote",
                                        value: "Request a Quote",
                                    },
                                },
                                { fields: { label: "Other", value: "Other" } },
                            ],
                        }}
                    />
                </StyledWrapper>
                {dynamicContent}
                <StyledWrapper {...styles.innerWideWrapper}>
                    <DynamicFormTextArea
                        fields={{
                            key: "message",
                            labelPosition: DynamicFormTextFieldLabelPositionsTop,
                            label: "Message",
                            hintText: "",
                            isRequired: true,
                            enableMaxLength: false,
                            maxLength: 10000,
                            rows: 5,
                        }}
                    />
                </StyledWrapper>
            </DynamicForm>
        </StyledWrapper>
    );
};

const formTab = {
    displayName: "Form",
    sortOrder: 0,
};

const buttonTab = {
    displayName: "Button",
    sortOrder: 1,
};

const actionTab = {
    displayName: "Actions",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(withHistory(ContactUsForm)),
    definition: {
        group: "Basic",
        displayName: "Authenticated Contact Us Form",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.emailRecipients,
                displayName: "Email recipients",
                editorTemplate: "MultilineTextField",
                fieldType: "General",
                defaultValue: "testaccount@insitesoft.com",
                isRequired: true,
                regularExpression: DynamicFormEmailRegExp,
                tab: formTab,
                sortOrder: 1,
            },
            {
                name: fields.successMessage,
                displayName: "Submit Success Message",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "Thank you for your inquiry. A customer service representative will contact you shortly.",
                tab: formTab,
                sortOrder: 2,
            },
            {
                name: fields.enableRecaptcha,
                displayName: "Include ReCaptcha",
                tooltip: "Enabling this setting adds a ReCaptcha element to the form which must be clicked to submit.",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 4,
                tab: formTab,
            },
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Submit",
                tab: buttonTab,
                sortOrder: 100,
            },
            {
                name: fields.buttonVariant,
                displayName: "Select Button Variant",
                editorTemplate: "DropDownField",
                defaultValue: DynamicFormButtonVariantsPrimary,
                fieldType: "General",
                options: [
                    {
                        displayName: "Primary",
                        value: DynamicFormButtonVariantsPrimary,
                    },
                    {
                        displayName: "Secondary",
                        value: DynamicFormButtonVariantsSecondary,
                    },
                    {
                        displayName: "Tertiary",
                        value: DynamicFormButtonVariantsTertiary,
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: buttonTab,
                sortOrder: 105,
            },
            {
                name: fields.buttonShape,
                displayName: "Select Button Shape",
                editorTemplate: "DropDownField",
                defaultValue: DynamicFormButtonShapesRectangle,
                fieldType: "General",
                options: [
                    {
                        displayName: "Rectangle",
                        value: DynamicFormButtonShapesRectangle,
                    },
                    {
                        displayName: "Pill",
                        value: DynamicFormButtonShapesPill,
                    },
                    {
                        displayName: "Rounded",
                        value: DynamicFormButtonShapesRounded,
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: buttonTab,
                sortOrder: 110,
            },
            {
                name: fields.buttonType,
                displayName: "Select Button Type",
                editorTemplate: "DropDownField",
                defaultValue: DynamicFormButtonTypesSolid,
                fieldType: "General",
                options: [
                    {
                        displayName: "Solid",
                        value: DynamicFormButtonTypesSolid,
                    },
                    {
                        displayName: "Outline",
                        value: DynamicFormButtonTypesOutline,
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: buttonTab,
                sortOrder: 115,
            },
            {
                name: fields.sendToThirdPartyApi,
                displayName: "Upload FormData to 3rd Party System",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 200,
                tab: actionTab,
            },
            {
                name: fields.sendToApiType,
                displayName: "Select 3rd Party System Type",
                editorTemplate: "DropDownField",
                defaultValue: DynamicFormThirdPartyApiTypesCustom,
                fieldType: "General",
                options: [
                    {
                        displayName: "Custom API Endpoint",
                        value: DynamicFormThirdPartyApiTypesCustom,
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                isVisible: widget => widget.fields.sendToThirdPartyApi,
                tab: actionTab,
                sortOrder: 210,
            },
            {
                name: fields.customApiEndpoint,
                displayName: "Custom API Endpoint URL",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                isVisible: widget =>
                    widget.fields.sendToThirdPartyApi &&
                    widget.fields.sendToApiType === DynamicFormThirdPartyApiTypesCustom,
                tab: actionTab,
                sortOrder: 220,
            },
            {
                name: fields.endpointRequiresAuth,
                displayName: "Upload Requires Authentication",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                isVisible: widget => widget.fields.sendToThirdPartyApi,
                tab: actionTab,
                sortOrder: 221,
            },
            {
                name: fields.endpointUserId,
                displayName: "User Id",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                isVisible: widget => widget.fields.endpointRequiresAuth,
                tab: actionTab,
                sortOrder: 222,
            },
            {
                name: fields.endpointPassword,
                displayName: "Password",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                isVisible: widget => widget.fields.endpointRequiresAuth,
                tab: actionTab,
                sortOrder: 223,
            },
            {
                name: fields.displayCustomApiResponse,
                displayName: "Display results from API below Form for testing",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 230,
                tab: actionTab,
            },
        ],
    },
};

export default widgetModule;
